<template>
  <v-container>
    <v-breadcrumbs large>
      <v-btn
        text
        class="text-primary"
        @click="back()"
      >
        <v-icon>mdi-keyboard-backspace</v-icon>
      </v-btn>
      {{ title }}
    </v-breadcrumbs>

    <v-alert
      v-if="rejectReason"
      type="warning"
      outlined
    >
      ຖືກປະຕິເສດ: {{ rejectReason }}
    </v-alert>

    <v-card>
      <v-card-text class="px-12">
        <v-form
          ref="form"
          lazy-validation
        >
          <template v-if="kind.isConversion">
            <p class="subtitle-1 font-weight-bold mb-2">
              1. ລູກຄ້າສາຍບ້ານທີ່ຈະປິດ
            </p>
            <v-row>
              <v-col cols="12">
                <v-autocomplete
                  v-model="homeCustomerId"
                  :items="homeItems"
                  :search-input.sync="homeSearch"
                  :loading="homeLoading"
                  :item-text="homeItemText"
                  item-value="id"
                  :item-disabled="(item) => !!item.open_conversion_id && item.open_conversion_id !== Number(id)"
                  label="ຄົ້ນຫາລູກຄ້າສາຍບ້ານ (CUS_, ຊື່, ເບີໂທ) *"
                  :rules="[required]"
                  :disabled="isEdit"
                  no-filter
                  outlined
                  dense
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>{{ homeItemText(item) }}</v-list-item-title>
                      <v-list-item-subtitle>
                        {{ item.village ? item.village.name : '' }}
                        <span v-if="item.package"> · {{ item.package.name }} ({{ Number(item.package.price).toLocaleString() }})</span>
                        <span
                          v-if="item.open_conversion_id"
                          class="warning--text"
                        > · ມີຄຳຂໍລໍຖ້າອະນຸມັດຢູ່ແລ້ວ</span>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
                <p class="errors">
                  {{ server_errors.home_customer_id }}
                </p>
              </v-col>
            </v-row>
            <v-alert
              type="info"
              text
              dense
            >
              ເມື່ອອະນຸມັດ: ລູກຄ້າສາຍບ້ານນີ້ຈະຖືກປິດ (inactive) ແລະ ລູກຄ້າທຸລະກິດໃໝ່ຈະຖືກສ້າງ, ໃຊ້ເບີໂທ login ໃໝ່.
            </v-alert>
            <p class="subtitle-1 font-weight-bold mb-2">
              2. ຂໍ້ມູນລູກຄ້າທຸລະກິດໃໝ່
            </p>
          </template>

          <v-row>
            <v-col
              cols="12"
              md="4"
            >
              <v-text-field
                v-model="form.requester_name"
                label="ຜູ້ປະສານງານຂໍເພີ່ມຂໍ້ມູນເຂົ້າໃນລະບົບ *"
                :rules="[required]"
                outlined
                dense
              />
              <p class="errors">
                {{ server_errors.requester_name }}
              </p>
            </v-col>
            <v-col
              cols="12"
              md="4"
            >
              <v-text-field
                v-model="form.coordinator_phone"
                label="ເບີຜູ້ປະສານງານ"
                type="number"
                class="input-number"
                outlined
                dense
              />
              <p class="errors">
                {{ server_errors.coordinator_phone }}
              </p>
            </v-col>
            <v-col
              cols="12"
              md="4"
            >
              <v-text-field
                v-model="form.requester_department"
                label="ພາກສ່ວນຜູ້ປະສານງານເພີ່ມຂໍ້ມູນເຂົ້າໃນລະບົບ *"
                :rules="[required]"
                outlined
                dense
              />
              <p class="errors">
                {{ server_errors.requester_department }}
              </p>
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <v-textarea
                v-model="form.conditions"
                label="ເງື່ອນໄຂ"
                rows="2"
                auto-grow
                outlined
                dense
              />
              <p class="errors">
                {{ server_errors.conditions }}
              </p>
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <v-file-input
                v-model="images"
                accept="image/*"
                multiple
                chips
                prepend-icon="mdi-cloud-upload"
                :label="existingMedia.length ? 'ເລືອກຮູບໃໝ່ (ແທນຮູບເກົ່າ)' : 'ຮູບພາບ'"
                outlined
                dense
              />
              <div v-if="existingMedia.length && !images.length">
                <v-avatar
                  v-for="img in existingMedia"
                  :key="img.id"
                  class="mr-2 rounded"
                  size="72px"
                >
                  <img :src="img.original_url || img.url">
                </v-avatar>
              </div>
              <p class="errors">
                {{ server_errors.images }}
              </p>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="4">
              <v-text-field
                v-model="form.company_name"
                label="ຊື່ບໍລິສັດ *"
                :rules="[required]"
                outlined
                dense
              />
              <p class="errors">
                {{ server_errors.company_name }}
              </p>
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="form.phone"
                label="ເບີໂທບໍລິສັດ * (020xxxxxxxx)"
                type="number"
                class="input-number"
                :rules="[required]"
                outlined
                dense
              />
              <p class="errors">
                {{ server_errors.phone }}
              </p>
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="form.email"
                label="Email ບໍລິສັດ"
                outlined
                dense
              />
              <p class="errors">
                {{ server_errors.email }}
              </p>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="6">
              <v-select
                v-model="form.status"
                :items="statusOptions"
                label="ສະຖານະອອກບິນ *"
                :rules="[required]"
                outlined
                dense
              />
              <p class="errors">
                {{ server_errors.status }}
              </p>
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="form.can_collect"
                :items="collectOptions"
                label="ສະຖານະເກັບ *"
                :rules="[required]"
                outlined
                dense
              />
              <p class="errors">
                {{ server_errors.can_collect }}
              </p>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="6">
              <v-autocomplete
                v-model="districtId"
                :items="districts"
                item-text="name"
                item-value="id"
                label="ເມືອງ *"
                outlined
                dense
              />
            </v-col>
            <v-col cols="6">
              <v-autocomplete
                v-model="form.village_id"
                :items="villages"
                item-text="name"
                item-value="id"
                label="ບ້ານ *"
                :rules="[required]"
                outlined
                dense
              />
              <p class="errors">
                {{ server_errors.village_id }}
              </p>
            </v-col>
          </v-row>
          <v-row>
            <v-col
              v-for="group in villageDetailGroups"
              :key="group.id"
              cols="6"
            >
              <v-select
                v-model="form.village_details"
                :items="group.village_details"
                item-text="name"
                item-value="id"
                :label="group.name"
                chips
                deletable-chips
                multiple
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="6">
              <v-select
                v-model="form.cost_by"
                :items="costs"
                item-text="name"
                item-value="value"
                label="ປະເພດບໍລິການ *"
                :rules="[required]"
                outlined
                dense
              />
              <p class="errors">
                {{ server_errors.cost_by }}
              </p>
            </v-col>
            <v-col v-if="needsFixCost">
              <v-text-field
                v-model="form.fix_cost"
                label="ມູນຄ່າສັນຍາ *"
                type="number"
                :rules="[required]"
                outlined
                dense
              />
              <p class="errors">
                {{ server_errors.fix_cost }}
              </p>
            </v-col>
          </v-row>

          <v-row v-if="form.cost_by === 'fix_cost'">
            <v-col>
              <v-row
                v-for="(row, index) in form.vehicle_types"
                :key="index"
              >
                <v-col>
                  <v-select
                    v-model="row.id"
                    label="ປະເພດລົດ"
                    :items="vehicleTypes"
                    item-text="name"
                    item-value="id"
                  />
                </v-col>
                <v-col>
                  <v-text-field
                    v-model="row.price"
                    type="number"
                    label="ລາຄາ"
                  />
                </v-col>
                <v-col cols="1">
                  <v-btn
                    icon
                    @click="form.vehicle_types.splice(index, 1)"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
              <v-btn
                color="blue"
                dark
                width="100%"
                @click="form.vehicle_types.push({ id: null, price: null })"
              >
                ເພີ່ມລາຄາຕາມລົດ
              </v-btn>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="4">
              <v-text-field
                v-model="form.expect_trash"
                :label="expectTrashLabel"
                type="number"
                outlined
                dense
              />
              <p class="errors">
                {{ server_errors.expect_trash }}
              </p>
            </v-col>
            <v-col cols="4">
              <v-menu
                v-model="startMenu"
                :close-on-content-click="true"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.start_date"
                    label="ເລີ່ມວັນທີ *"
                    readonly
                    :rules="[required]"
                    outlined
                    dense
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker v-model="form.start_date" />
              </v-menu>
              <p class="errors">
                {{ server_errors.start_date }}
              </p>
            </v-col>
            <v-col cols="4">
              <v-select
                v-model="form.favorite_dates"
                :items="favoriteDates"
                item-text="name"
                item-value="name"
                label="ມື້ບໍລິການ"
                multiple
                outlined
                dense
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <v-text-field
                v-model="form.collect_description"
                label="ໝາຍເຫດ"
                outlined
                dense
              />
            </v-col>
          </v-row>

          <v-row v-if="!isCompanyEdit">
            <v-col cols="6">
              <v-text-field
                v-model="form.password"
                :label="isEdit ? 'Password ໃໝ່ (ປະວ່າງ = ໃຊ້ອັນເກົ່າ)' : 'Password *'"
                type="password"
                :rules="isEdit ? [] : passwordRules"
                outlined
                dense
              />
              <p class="errors">
                {{ server_errors.password }}
              </p>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="form.password_confirmation"
                label="Password Confirm"
                type="password"
                :rules="isEdit && !form.password ? [] : passwordRules"
                outlined
                dense
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="latlng.lat"
                label="Latitude"
                type="number"
                class="input-number"
                outlined
                dense
              />
              <p class="errors">
                {{ server_errors.lat }}
              </p>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="latlng.lng"
                label="Longitude"
                type="number"
                class="input-number"
                outlined
                dense
              />
              <p class="errors">
                {{ server_errors.lng }}
              </p>
            </v-col>
            <v-col cols="12">
              <gmap-autocomplete
                class="input text-field"
                placeholder="ຄົ້ນຫາເເຜນທີ່..."
                :options="{ fields: ['geometry', 'formatted_address', 'name'] }"
                @place_changed="setPlace"
              />
            </v-col>
            <v-col
              cols="12"
              class="mb-4"
            >
              <GmapMap
                :center="mapCenter"
                :zoom="17"
                style="width: 100%; height: 400px"
                :disable-default-u-i="true"
              >
                <GmapMarker
                  :position="mapCenter"
                  :draggable="true"
                  @dragend="onDragEnd"
                />
              </GmapMap>
            </v-col>
          </v-row>
        </v-form>

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="blue darken-1"
            text
            @click="back()"
          >
            Back
          </v-btn>
          <v-btn
            color="blue darken-1"
            text
            :loading="loading"
            :disabled="loading"
            @click="save()"
          >
            {{ isEdit ? 'ສົ່ງຂໍອະນຸມັດໃໝ່' : 'ສົ່ງຂໍອະນຸມັດ' }}
          </v-btn>
        </v-card-actions>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { getCustomerUnit } from '@/Helpers/Customer';
import {
  checkLaosPair, DEFAULT_MAP_CENTER, gpsFieldValue, hasGpsPoint,
} from '@/utils/laosCoordinate.mjs';
import { submissionKind, homeLabel } from './submissionKinds';

export default {
  // Three uses, set by the route's meta.formKind:
  // 'submission' = a new company for approval; 'edit' = an edit of an existing company for approval;
  // 'convert' = close a household customer and open a commercial one, for approval.
  props: {
    id: {
      type: [String, Number],
      default: null,
    },
    customerId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      server_errors: {},
      rejectReason: '',
      existingMedia: [],
      images: [],
      districts: [],
      districtId: null,
      villages: [],
      villageDetailGroups: [],
      vehicleTypes: [],
      favoriteDates: [],
      startMenu: false,
      // GPS fields: empty until staff drag the pin or pick a place (the map centre is not a value).
      latlng: { lat: '', lng: '' },
      homeCustomerId: null,
      homeItems: [],
      homeSearch: null,
      homeLoading: false,
      homeSeq: 0,
      form: {
        requester_name: '',
        coordinator_phone: '',
        requester_department: '',
        conditions: '',
        company_name: '',
        phone: '',
        email: '',
        // A new company opens active and collectable unless the sender changes it.
        status: 'active',
        can_collect: '1',
        village_id: null,
        village_details: [],
        cost_by: '',
        fix_cost: '',
        vehicle_types: [],
        expect_trash: '',
        start_date: '',
        favorite_dates: [],
        collect_description: '',
        password: '',
        password_confirmation: '',
      },
      statusOptions: [
        { value: 'active', text: 'active' },
        { value: 'inactive', text: 'inactive' },
      ],
      // Strings: the API returns the current value as "1" / "0".
      collectOptions: [
        { value: '1', text: 'ເກັບໄດ້' },
        { value: '0', text: 'ເກັບບໍ່ໄດ້' },
      ],
      costs: [
        { value: 'container', name: 'ຄອນເທັນເນີ' },
        { value: 'fix_cost', name: 'ທຸລະກິດເປັນຖ້ຽວ' },
        { value: 'chartered', name: 'ມອບເໝົາ' },
        { value: 'bag', name: 'ບໍລິມາດ' },
        { value: '32km', name: 'ຫຼັກ32' },
        { value: 'infect', name: 'ຂີ້ເຫຍື້ອຕິດເຊື້ອ' },
      ],
      required: (v) => (v !== null && v !== undefined && v !== '') || 'ກະລຸນາປ້ອນຂໍ້ມູນ',
      passwordRules: [
        (v) => !!v || 'Password is required',
        (v) => (v && v.length >= 8) || 'Password must be at least 8 characters',
      ],
    };
  },
  computed: {
    isEdit() {
      return !!this.id;
    },
    isCompanyEdit() {
      return this.$route.meta.formKind === 'edit';
    },
    kind() {
      return submissionKind(this.$route);
    },
    title() {
      if (this.isCompanyEdit) return this.isEdit ? 'ແກ້ໄຂຄຳຂໍ ແລະ ສົ່ງຂໍອະນຸມັດໃໝ່' : 'ແກ້ໄຂຂໍ້ມູນລູກຄ້າທຸລະກິດ (ລໍຖ້າອະນຸມັດ)';
      return this.isEdit ? 'ແກ້ໄຂ ແລະ ສົ່ງຂໍອະນຸມັດໃໝ່' : this.kind.createTitle;
    },
    requestUrl() {
      return this.isCompanyEdit ? 'company-edit-request' : this.kind.api;
    },
    needsFixCost() {
      return this.form.cost_by && !['bag', '32km'].includes(this.form.cost_by);
    },
    expectTrashLabel() {
      const text = 'ຂີ້ເຫຍື້ອຄາດໝາຍ/ອາທິດ';
      return this.form.cost_by ? `${text} (${getCustomerUnit(this.form.cost_by)})` : text;
    },
    // Where the map and pin sit: the typed point, else Vientiane centre (display only).
    mapCenter() {
      const lat = parseFloat(this.latlng.lat);
      const lng = parseFloat(this.latlng.lng);
      if (hasGpsPoint(this.latlng.lat, this.latlng.lng) && Number.isFinite(lat) && Number.isFinite(lng)) {
        return { lat, lng };
      }
      return DEFAULT_MAP_CENTER;
    },
  },
  watch: {
    districtId(value, old) {
      if (old !== null) this.form.village_id = null;
      this.fetchVillages();
    },
    'form.village_id': function () {
      this.fetchVillageDetails();
    },
    'latlng.lat': function () {
      this.$set(this.server_errors, 'lat', '');
    },
    'latlng.lng': function () {
      this.$set(this.server_errors, 'lng', '');
    },
    // The same component serves several routes; vue-router reuses it when switching.
    '$route.fullPath': function () {
      Object.assign(this.$data, this.$options.data.call(this));
      if (this.$refs.form) this.$refs.form.resetValidation();
      this.init();
    },
    homeSearch(value) {
      if (this.kind.isConversion && !this.isEdit) this.fetchHomeCustomers(value);
    },
  },
  async created() {
    await this.init();
  },
  methods: {
    async init() {
      await Promise.all([this.fetchDistricts(), this.fetchLookups()]);
      if (this.isEdit) await this.fetchRequest();
      else if (this.isCompanyEdit) await this.fetchCurrent();
      else if (this.districts.length) this.districtId = this.districts[0].id;
      if (this.kind.isConversion && !this.isEdit) this.fetchHomeCustomers('');
    },
    back() {
      this.$router.push({ name: this.isCompanyEdit ? 'CompanyEditRequest' : this.kind.listRoute });
    },
    homeItemText(item) {
      return item ? [homeLabel(item), item.user ? item.user.phone : ''].filter(Boolean).join(' · ') : '';
    },
    /** Household customers that can be closed (server search). */
    fetchHomeCustomers(filter) {
      const seq = ++this.homeSeq;
      this.homeLoading = true;
      this.$axios
        .get('home-conversion/home-customers', { params: { filter: filter || undefined, per_page: 20 } })
        .then((res) => {
          if (seq !== this.homeSeq) return;
          const found = res.data.data.data;
          // Keep the chosen one in the list, or the field would go blank.
          const chosen = this.homeItems.find((item) => item.id === this.homeCustomerId);
          this.homeItems = chosen && !found.some((item) => item.id === chosen.id) ? [chosen, ...found] : found;
        })
        .catch(() => {})
        .finally(() => {
          if (seq === this.homeSeq) this.homeLoading = false;
        });
    },
    async fetchDistricts() {
      const res = await this.$axios.get('info/address', { params: { filter: 'ນະຄອນຫລວງວຽງຈັນ' } });
      const province = (res.data.data || [])[0];
      this.districts = province ? province.districts : [];
    },
    async fetchLookups() {
      // Optional lists: a failure must not stop the district/village loading.
      const [vehicleTypes, favoriteDates] = await Promise.all([
        this.$axios.get('vehicle_type/').catch(() => null),
        this.$axios.get('favorite-date').catch(() => null),
      ]);
      this.vehicleTypes = vehicleTypes ? vehicleTypes.data.data : [];
      this.favoriteDates = favoriteDates ? favoriteDates.data.data : [];
    },
    async fetchVillages() {
      if (!this.districtId) return;
      const res = await this.$axios.get(`info/district/${this.districtId}/village`);
      this.villages = res.data.data;
      if (!this.form.village_id && this.villages.length) this.form.village_id = this.villages[0].id;
    },
    async fetchVillageDetails() {
      if (!this.form.village_id) {
        this.villageDetailGroups = [];
        return;
      }
      const res = await this.$axios.get(`info/village/${this.form.village_id}/village-detail`);
      this.villageDetailGroups = res.data.data;
    },
    /** An existing submission / edit request (to fix and resubmit). */
    async fetchRequest() {
      this.$store.commit('Loading_State', true);
      try {
        const res = await this.$axios.get(`${this.requestUrl}/${this.id}`);
        const request = res.data.data;
        this.rejectReason = request.reject_reason;
        this.applyValues(request.payload || {}, request.district_id, request.media || []);
        this.form.requester_name = request.requester_name || '';
        this.form.requester_department = request.requester_department || '';
        this.form.conditions = request.conditions || '';
        if (request.home_customer) {
          this.homeItems = [request.home_customer];
          this.homeCustomerId = request.home_customer.id;
        }
      } finally {
        this.$store.commit('Loading_State', false);
      }
    },
    /** The company's current values, to start an edit from. */
    async fetchCurrent() {
      this.$store.commit('Loading_State', true);
      try {
        const res = await this.$axios.get(`company-edit-request/current/${this.customerId}`);
        const current = res.data.data;
        this.applyValues(current.values || {}, current.district_id, current.media || []);
      } finally {
        this.$store.commit('Loading_State', false);
      }
    },
    applyValues(values, districtId, media) {
      Object.keys(this.form).forEach((key) => {
        if (values[key] !== undefined && values[key] !== null) this.form[key] = values[key];
      });
      // Multipart posts store ids as strings; the selects match numbers.
      this.form.village_id = values.village_id ? Number(values.village_id) : null;
      this.form.village_details = (values.village_details || []).map(Number);
      this.form.vehicle_types = (values.vehicle_types || []).map((row) => ({ id: Number(row.id), price: row.price }));
      this.latlng = { lat: gpsFieldValue(values.lat), lng: gpsFieldValue(values.lng) };
      this.existingMedia = media;
      // Setting the district loads its villages; keep the saved village.
      this.districtId = districtId;
    },
    setPlace(place) {
      if (!place || !place.geometry) return;
      this.latlng = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() };
    },
    onDragEnd(evt) {
      this.latlng = { lat: evt.latLng.lat(), lng: evt.latLng.lng() };
    },
    buildFormData() {
      const fd = new FormData();
      const append = (key, value) => {
        if (value !== null && value !== undefined && value !== '') fd.append(key, value);
      };
      const scalarFields = [
        'company_name', 'phone', 'email', 'coordinator_phone', 'status', 'can_collect', 'village_id', 'cost_by', 'expect_trash',
        'start_date', 'collect_description', 'password', 'password_confirmation',
        'requester_name', 'requester_department', 'conditions',
      ];
      scalarFields.forEach((key) => append(key, this.form[key]));
      if (this.needsFixCost) append('fix_cost', this.form.fix_cost);
      if (this.kind.isConversion && !this.isCompanyEdit) append('home_customer_id', this.homeCustomerId);
      // GPS is optional on all three kinds: unknown goes as '' for both (stored as NULL).
      fd.append('lat', gpsFieldValue(this.latlng.lat));
      fd.append('lng', gpsFieldValue(this.latlng.lng));
      this.form.village_details.forEach((value) => fd.append('village_details[]', value));
      this.form.favorite_dates.forEach((value) => fd.append('favorite_dates[]', value));
      if (this.form.cost_by === 'fix_cost') {
        this.form.vehicle_types
          .filter((row) => row.id && row.price)
          .forEach((row, index) => {
            fd.append(`vehicle_types[${index}][id]`, row.id);
            fd.append(`vehicle_types[${index}][price]`, row.price);
          });
      }
      this.images.forEach((file) => fd.append('images[]', file));
      return fd;
    },
    // Same rule as the server (LaosCoordinate); shows the Lao message under the field.
    checkGps() {
      const errors = checkLaosPair(this.latlng.lat, this.latlng.lng);
      this.$set(this.server_errors, 'lat', errors.lat);
      this.$set(this.server_errors, 'lng', errors.lng);
      return !errors.lat && !errors.lng;
    },
    async save() {
      const formOk = this.$refs.form.validate();
      const gpsOk = this.checkGps();
      if (!formOk || !gpsOk) return;
      this.server_errors = {};
      this.loading = true;
      this.$store.commit('Loading_State', true);
      let url = this.isEdit ? `${this.requestUrl}/${this.id}/resubmit` : this.requestUrl;
      if (this.isCompanyEdit && !this.isEdit) url = `company-edit-request/for/${this.customerId}`;
      try {
        const res = await this.$axios.post(url, this.buildFormData(), {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        this.$store.commit('Toast_State', { value: true, color: 'success', msg: res.data.message });
        this.back();
      } catch (error) {
        const response = error.response || {};
        this.$store.commit('Toast_State', {
          value: true,
          color: 'error',
          msg: (response.data && response.data.message) || 'Something went wrong',
        });
        if (response.status === 422) {
          const errors = response.data.errors || {};
          Object.entries(errors).forEach(([key, messages]) => {
            // vehicle_types.0.price → vehicle_types, images.0 → images
            this.$set(this.server_errors, key.split('.')[0], Array.isArray(messages) ? messages[0] : messages);
          });
        }
      } finally {
        this.loading = false;
        this.$store.commit('Loading_State', false);
      }
    },
  },
};
</script>

<style lang="scss">
@import "../../../public/scss/main.scss";

.text-field {
  border-color: $primary-color;
  padding: 12px 8px;
  width: 100%;
  margin-bottom: 12px;
  font-size: 16px;
  background: #eee;
  border-radius: 2px;
}
</style>
